import React, { useState } from "react";
import { estateMappingDocumentUrl } from "../api";
import { formatMoney } from "../utils";
import SectionIcon from "./SectionIcon";
import WealthMapPreview from "./WealthMapPreview";

const reviewGroupTitle = (copy, sectionId) => {
  if (["estructuras", "arbol_familiar"].includes(sectionId)) return copy.review.structures_and_family;
  return sectionId === "pasivos" ? copy.review.liabilities : copy.review.assets;
};

const formatReviewValue = (key, value, item) => {
  if (["valuation", "valor", "principal", "valor_poliza", "indemnizacion", "prima"].includes(key)) return formatMoney(value, item.currency_type || item.moneda);
  if (Array.isArray(value)) {
    return value.map((record) => (
      record && typeof record === "object" ? record.value || `${record.type}: ${record.id}` : record
    )).join(", ");
  }
  return value;
};

const itemTitle = (sectionId, section, item, fallback) => (
  sectionId === "cuentas_inversion"
    ? item.numero_cuenta || fallback
    : item[section.nameKey] || item.nombre || fallback
);

const itemDetailKeys = (sectionId, section, item) => {
  const keys = Object.keys(item).filter((key) => (
    item[key] &&
    (sectionId === "cuentas_inversion" || key !== section.nameKey) &&
    key !== "estate_mapping_id" &&
    key !== "herederos" &&
    key !== "reviewIndex"
  ));
  if (sectionId !== "cuentas_inversion") return keys;

  const firstKeys = ["valuation", "valor", "custodio"];
  return firstKeys.filter((key) => keys.includes(key)).concat(
    keys.filter((key) => !firstKeys.includes(key) && key !== "numero_cuenta" && key !== "currency_type")
  );
};

const ReviewStep = ({ copy, data, documents = [], fieldLabels, internalization, ownershipStructure, order, sections, selected, onBack, onEdit, onAddSection, onOwnershipStructureChange, onSubmit }) => {
  const [openSection, setOpenSection] = useState(selected[0]);
  const unselected = order.filter((id) => !selected.includes(id));
  const reviewSectionIds = order.filter((sectionId) => selected.includes(sectionId));

  return (
    <section className="estate-mapping__content">
      <h2>{copy.review.title}</h2>
      <p>{copy.review.body}</p>
      <div className="estate-mapping__review-list">
        {reviewSectionIds.map((sectionId, index) => {
          const section = sections[sectionId];
          const items = data[sectionId] || [];
          const isOpen = openSection === sectionId;
          const groupTitle = reviewGroupTitle(copy, sectionId);
          const previousGroupTitle = index > 0 ? reviewGroupTitle(copy, reviewSectionIds[index - 1]) : null;
          return (
            <React.Fragment key={sectionId}>
              {groupTitle !== previousGroupTitle && <div className="estate-mapping__pillar-label">{groupTitle}</div>}
              <div className={`estate-mapping__review-section${isOpen ? " is-open" : ""}`}>
                <button type="button" onClick={() => setOpenSection((current) => current === sectionId ? null : sectionId)}>
                  <SectionIcon section={section} small />
                  <span className="estate-mapping__review-section-title"><strong>{section.title} ({items.length})</strong></span>
                  <i>v</i>
                </button>
                {isOpen && (
                  <div>
                    {items.length === 0 && <div className="estate-mapping__review-empty">{copy.review.omitted}</div>}
                    {items.map((item, itemIndex) => {
                      const name = itemTitle(sectionId, section, item, copy.list.no_name);
                      const itemDocuments = documents.filter((document) => document.estate_mapping_record_id === item.estate_mapping_id);
                      return (
                        <div className="estate-mapping__review-item" key={`${sectionId}-${itemIndex}`}>
                          <div><strong>{name}</strong><button type="button" onClick={() => onEdit(sectionId, itemIndex)}>{copy.buttons.edit}</button></div>
                          <dl>
                            {itemDetailKeys(sectionId, section, item).map((key) => (
                              <React.Fragment key={key}>
                                <dt>{key === "notes" ? copy.form.notes : fieldLabels[key] || key}</dt>
                                <dd>{formatReviewValue(key, item[key], item)}</dd>
                              </React.Fragment>
                            ))}
                          </dl>
                          {itemDocuments.length > 0 && (
                            <div className="estate-mapping__review-documents">
                              <span>{copy.form.documents}</span>
                              {itemDocuments.map((document) => (
                                <a href={estateMappingDocumentUrl(document)} key={document.id} rel="noopener noreferrer" target="_blank">{document.file_name}</a>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {unselected.length > 0 && (
        <div className="estate-mapping__add-section">
          <span>{copy.review.add_unselected}</span>
          <div>{unselected.map((id) => <button key={id} type="button" onClick={() => onAddSection(id)}><SectionIcon section={sections[id]} small />{sections[id].title}</button>)}</div>
        </div>
      )}
      {ownershipStructure.nodeDataArray.length > 0 && <><div className="estate-mapping__pillar-label estate-mapping__pillar-label--wealth-map">{copy.review.wealth_map_preview}</div>
        <WealthMapPreview internalization={internalization} ownershipStructure={ownershipStructure} onOwnershipStructureChange={onOwnershipStructureChange} />
      </>}
      <div className="estate-mapping__notice">{copy.review.notice}</div>
      <div className="estate-mapping__footer-actions">
        <button className="estate-mapping__button estate-mapping__button--secondary" onClick={onBack}>{copy.buttons.previous}</button>
        <button className="estate-mapping__button estate-mapping__button--primary" onClick={onSubmit}>{copy.review.submit}</button>
      </div>
    </section>
  );
};

export default ReviewStep;
